'use client'

import * as React from 'react'
import { Command } from 'lucide-react'

import { cn } from '../../../lib/utils'
import {
  formatShortcut,
  isMacPlatform,
  type ShortcutKey,
} from '../../../lib/keyboard-shortcuts'
import { Kbd, KbdGroup } from './index'

export type ShortcutKeysProps = {
  keys: ShortcutKey[]
  className?: string
  variant?: 'keys' | 'hint'
}

const HINT_PART_BOX =
  'inline-flex h-3.5 items-center justify-center leading-none'

const HINT_TEXT_CLASS =
  'font-sans text-[13px] font-medium text-muted-foreground/50 antialiased'

function ShortcutHintPart({ value }: { value: string }) {
  if (value === '⌘') {
    return (
      <span className={cn(HINT_PART_BOX, 'w-3')}>
        <Command
          className="size-3 shrink-0 text-muted-foreground/50"
          strokeWidth={2.25}
          aria-hidden
        />
      </span>
    )
  }

  if (value.length === 1) {
    return (
      <span
        className={cn(HINT_PART_BOX, HINT_TEXT_CLASS, 'min-w-3 font-semibold')}
      >
        {value}
      </span>
    )
  }

  return (
    <span className={cn(HINT_PART_BOX, HINT_TEXT_CLASS, 'min-w-0')}>
      {value}
    </span>
  )
}

function ShortcutKeys({
  keys,
  className,
  variant = 'keys',
}: ShortcutKeysProps) {
  if (variant === 'hint') {
    const parts = formatShortcut(keys)
    const separator = isMacPlatform() ? null : '+'

    return (
      <span
        className={cn(
          'pointer-events-none shrink-0 inline-flex items-center transition-colors duration-200',
          'group-hover/nav-item:[&_svg]:text-muted-foreground/70 group-hover/nav-item:[&_span]:text-muted-foreground/70',
          className,
        )}
      >
        <span className="inline-flex items-center gap-0.5">
          {parts.map((part, index) => (
            <React.Fragment key={`${part.value}-${index}`}>
              {index > 0 && separator ? (
                <span
                  className={cn(
                    HINT_PART_BOX,
                    'min-w-0 font-sans text-[10px] text-muted-foreground/40',
                  )}
                >
                  {separator}
                </span>
              ) : null}
              <ShortcutHintPart value={part.value} />
            </React.Fragment>
          ))}
        </span>
      </span>
    )
  }

  const parts = formatShortcut(keys)

  return (
    <KbdGroup className={className}>
      {parts.map((part, index) => (
        <React.Fragment key={`${part.value}-${index}`}>
          {index > 0 ? (
            <span className="text-muted-foreground text-[10px]">+</span>
          ) : null}
          <Kbd>{part.value}</Kbd>
        </React.Fragment>
      ))}
    </KbdGroup>
  )
}

export { ShortcutKeys }
